import React, { useEffect, useRef } from 'react';
import { formatDate } from '../analyticsUtils';
import {
  Btn,
  Card,
  CardHead,
  GhostBtn,
  Hint,
  Input,
  Mono,
  Row,
  Select,
  Stack,
  StatTile,
  Table,
  TableWrap,
  VisuallyHidden,
} from '../AnalyticsAdmin.styles';
import RowMenu from './RowMenu';

const WorkspaceView = ({
  workspace,
  projects,
  members,
  currentUserEmail,
  canManage,
  retentionDays,
  onOpenProject,
  onRenameWorkspace,
  onRenameProject,
  onRotateKey,
  onDeleteProject,
  copyText,
  newProjectOpen,
  setNewProjectOpen,
  newProjectName,
  setNewProjectName,
  creatingProject,
  onCreateProject,
  memberEmail,
  setMemberEmail,
  memberRole,
  setMemberRole,
  memberSaving,
  onAddMember,
  onRemoveMember,
}) => {
  const newProjectRef = useRef(null);
  useEffect(() => {
    if (newProjectOpen) newProjectRef.current?.focus();
  }, [newProjectOpen]);

  if (!workspace) {
    return <Hint>No workspace selected.</Hint>;
  }

  const maxProjects = workspace.limits?.maxProjects ?? 1;
  const maxMembers = workspace.limits?.maxMembers ?? 3;
  const atProjectLimit = Number(workspace.project_count || projects.length) >= maxProjects;

  return (
    <Stack $gap="1.25rem">
      <Row $gap="0.75rem">
        <StatTile>
          <span>Plan</span>
          <strong>{workspace.plan_name || workspace.plan_id || 'Starter'}</strong>
        </StatTile>
        <StatTile>
          <span>Projects</span>
          <strong>
            {workspace.project_count ?? projects.length} <small>/ {maxProjects}</small>
          </strong>
        </StatTile>
        <StatTile>
          <span>Members</span>
          <strong>
            {workspace.member_count ?? members.length} <small>/ {maxMembers}</small>
          </strong>
        </StatTile>
        <StatTile>
          <span>Default retention</span>
          <strong>
            {retentionDays} <small>days</small>
          </strong>
        </StatTile>
      </Row>

      <Card $flush>
        <CardHead>
          <h2>Projects</h2>
          <Row>
            {canManage && (
              <GhostBtn type="button" $small onClick={onRenameWorkspace}>
                Rename workspace
              </GhostBtn>
            )}
            {canManage && !newProjectOpen && (
              <Btn
                type="button"
                onClick={() => setNewProjectOpen(true)}
                disabled={atProjectLimit}
                title={atProjectLimit ? 'Project limit reached for this plan' : undefined}
                style={{ minHeight: '2.125rem', fontSize: '0.8125rem' }}
              >
                + New project
              </Btn>
            )}
          </Row>
        </CardHead>

        {newProjectOpen && (
          <form onSubmit={onCreateProject} style={{ padding: '0.875rem 1.125rem', borderBottom: '1px solid var(--border)' }}>
            <Row>
              <VisuallyHidden as="label" htmlFor="new-project-name">Project name</VisuallyHidden>
              <Input
                id="new-project-name"
                ref={newProjectRef}
                placeholder="Project name, e.g. Survive the Cold"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                $grow
              />
              <Btn type="submit" disabled={creatingProject || !newProjectName.trim()}>
                {creatingProject ? 'Creating…' : 'Create project'}
              </Btn>
              <GhostBtn type="button" onClick={() => setNewProjectOpen(false)}>
                Cancel
              </GhostBtn>
            </Row>
          </form>
        )}

        {projects.length === 0 ? (
          <Hint style={{ padding: '1.25rem 1.125rem', margin: 0 }}>No projects yet. Create one to get an API key for your game.</Hint>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Project ID</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project.id}>
                    <td className="strong">{project.name}</td>
                    <td>
                      <Mono>{project.id}</Mono>
                    </td>
                    <td>{formatDate(project.created_at)}</td>
                    <td className="actions">
                      <Row $justify="flex-end" style={{ flexWrap: 'nowrap' }}>
                        <GhostBtn type="button" $small onClick={() => onOpenProject(project.id)}>
                          Open
                        </GhostBtn>
                        <RowMenu
                          label={`More actions for ${project.name}`}
                          items={[
                            { label: 'Copy project ID', onClick: () => copyText(project.id) },
                            { label: 'Rotate API key', onClick: () => onRotateKey(project.id) },
                            canManage && { label: 'Rename', onClick: () => onRenameProject(project) },
                            canManage && { label: 'Delete project…', danger: true, onClick: () => onDeleteProject(project) },
                          ]}
                        />
                      </Row>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      <Card $flush>
        <CardHead>
          <h2>Members</h2>
          <Mono>
            {members.length} / {maxMembers}
          </Mono>
        </CardHead>
        <TableWrap>
          <Table $min="28rem">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={`${member.workspace_id}:${member.user_email}`}>
                  <td className="strong">{member.user_email}</td>
                  <td style={{ textTransform: 'capitalize' }}>{member.role}</td>
                  <td>{formatDate(member.created_at)}</td>
                  <td className="actions">
                    {canManage && member.role !== 'owner' && member.user_email?.toLowerCase() !== currentUserEmail?.toLowerCase() && (
                      <GhostBtn type="button" $small onClick={() => onRemoveMember(member.user_email)}>
                        Remove
                      </GhostBtn>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
        {canManage && (
          <form onSubmit={onAddMember} style={{ padding: '0.875rem 1.125rem', borderTop: '1px solid var(--border)' }}>
            <Row>
              <VisuallyHidden as="label" htmlFor="member-email">Teammate email</VisuallyHidden>
              <Input
                id="member-email"
                type="email"
                placeholder="teammate@example.com"
                value={memberEmail}
                onChange={(e) => setMemberEmail(e.target.value)}
                $grow
              />
              <Select aria-label="Role" value={memberRole} onChange={(e) => setMemberRole(e.target.value)}>
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
              </Select>
              <Btn type="submit" disabled={memberSaving || !memberEmail.trim()}>
                {memberSaving ? 'Adding…' : 'Add member'}
              </Btn>
            </Row>
          </form>
        )}
      </Card>
    </Stack>
  );
};

export default WorkspaceView;
